import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, HostBinding, Input } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import { MediaRootDetail, UnfilteredTag } from 'elements-sdk'
import { debounceTime, distinctUntilChanged, mergeMap, from, map, noop, OperatorFunction } from 'rxjs'

import { APIService } from 'api/module'
import { BaseComponent } from 'common/helpers'
import { IdentityService } from 'main/services/identity.service'

@Component({
  selector: 'media-tags-input',
  templateUrl: './mediaTagsInput.component.pug',
  styleUrls: ['./mediaTagsInput.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'input-group flex-wrap pl-2',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MediaTagsInputComponent),
      multi: true,
    },
  ],
})

export class MediaTagsInputComponent extends BaseComponent implements ControlValueAccessor {
  @Input() allowNew = false
  @Input() root?: MediaRootDetail

  value: UnfilteredTag[] = []
  knownTags: Record<string, UnfilteredTag|undefined> = {}

  @HostBinding('attr.disabled') get disabledAttribute () {
    return this.controlDisabled ? '' : null
  }
  controlDisabled = false
  addLock = Promise.resolve()
  autocompleteText = ''

  constructor (
    private api: APIService,
    private changeDetector: ChangeDetectorRef,
    private identity: IdentityService,
  ) {
    super()
  }

  typeahead: OperatorFunction<string, readonly UnfilteredTag[]> = (text$) => {
    return text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      mergeMap((term: string) => !term ? [] : from(this.getAutocompletions(term))),
      map(result => result.slice(0, 10)),
    )
  }

  async getAutocompletions (query: string) {
    const tags = await this.api.mediaLibrary.getAllMediaTags({
      nameIcontains: query,
      forRoot: this.root?.id,
      limit: 20,
    })
    for (const tag of tags) {
      this.knownTags[this.normalizeName(tag.name)] = tag
    }
    return tags.filter(x => !this.value.some(y => y.id === x.id))
  }

  writeModel () {
    this.onChangeHandler(this.value)
    this.onTouchHandler(this.value)
  }

  addExisting (tag: UnfilteredTag) {
    if (this.value.some(x => x.id === tag.id)) {
      return
    }
    this.value.push(tag)
    this.autocompleteText = ''
    this.writeModel()
    this.changeDetector.markForCheck()
  }

  addNew (name: string) {
    if (!name) {
      return
    }
    this.autocompleteText = ''
    const result = this.addLock.then(() => this.addNewInternal(name))
    this.addLock = result.catch(noop)
  }

  async addNewInternal (name: string) {
    if (!this.allowNew) {
      return
    }
    name = this.normalizeName(name)
    if (this.knownTags[name]) {
      this.addExisting(this.knownTags[name]!)
      return
    }

    const tags = await this.api.mediaLibrary.getAllMediaTags({ name })
    if (tags.length) {
      this.addExisting(tags[0])
      return
    }

    const tag = await this.api.mediaLibrary.createMediaTag({
      unfilteredTagUpdate: {
        name,
        roots: this.root ? [this.root.id] : [],
        shared: !this.root,
      },
    })
    this.addExisting(tag)
  }

  remove (tag: UnfilteredTag) {
    this.value = this.value.filter(x => x.id !== tag.id)
    this.writeModel()
    this.changeDetector.markForCheck()
  }

  normalizeName (name) {
    // Replicate mysql utf8_general_ci collationa behaviour
    //
    // Remove accents by decomposing into chars with combining marks (NFD)
    // after that we remove every combining mark (u0300-u036f) and return
    // the string in lower case
    return name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  }

  // ControlValueAccessor
  private onChangeHandler: any = noop
  private onTouchHandler: any = noop

  writeValue (value) {
    if (this.value !== value) {
      this.changeDetector.markForCheck()
    }
    value ??= []
    this.value = value.slice()

    this.value.forEach(x => {
      this.knownTags[this.normalizeName(x.name)] = x
    })
  }

  registerOnChange (fn) {
    this.onChangeHandler = fn
  }

  registerOnTouched (fn) {
    this.onTouchHandler = fn
  }

  setDisabledState (isDisabled) {
    this.controlDisabled = isDisabled
    this.changeDetector.markForCheck()
  }

  // HACK: temporary implementation of permissionEnable directive for ngUpgrade templates
  // eslint-disable-next-line accessor-pairs
  @Input('permissionenable') set requirePermission (permission: string|undefined) {
    if (permission) {
      if (!this.identity.hasPermission(permission)) {
        this.setDisabledState(true)
      }
    }
  }
}
